/* Syntax highlighting — tokenizer approach (no regex-on-HTML) */

import { esc } from './state.js';

const PY_KEYWORDS = new Set([
  "def","class","if","elif","else","for","while","return","import","from",
  "as","try","except","finally","with","raise","yield","lambda","and","or",
  "not","in","is","True","False","None","pass","break","continue","global",
  "nonlocal","assert","del",
]);

const PY_BUILTINS = new Set([
  "print","len","range","str","int","float","list","dict","set","tuple",
  "type","isinstance","getattr","setattr","hasattr","super","property",
  "staticmethod","classmethod","Trace","enumerate","zip","map","filter",
  "sorted","reversed","any","all","min","max","abs","sum","open","repr",
]);

export function highlightPython(code) {
  const tokens = [];
  let i = 0;

  while (i < code.length) {
    // --- Comment (# to end of line) ---
    if (code[i] === "#") {
      const end = code.indexOf("\n", i);
      const slice = end === -1 ? code.substring(i) : code.substring(i, end);
      tokens.push({ cls: "py-comment", text: slice });
      i += slice.length;
      continue;
    }

    // --- Triple-quoted strings ---
    if (code.substring(i, i + 3) === '"""' || code.substring(i, i + 3) === "'''") {
      const q = code.substring(i, i + 3);
      const end = code.indexOf(q, i + 3);
      const slice = end === -1 ? code.substring(i) : code.substring(i, end + 3);
      tokens.push({ cls: "py-str", text: slice });
      i += slice.length;
      continue;
    }

    // --- Single/double quoted strings ---
    if (code[i] === '"' || code[i] === "'") {
      const q = code[i];
      let j = i + 1;
      while (j < code.length && code[j] !== q && code[j] !== "\n") {
        if (code[j] === "\\") j++;
        j++;
      }
      if (j < code.length && code[j] === q) j++;
      tokens.push({ cls: "py-str", text: code.substring(i, j) });
      i = j;
      continue;
    }

    // --- Numbers ---
    if (/\d/.test(code[i]) && (i === 0 || !/\w/.test(code[i - 1]))) {
      let j = i;
      while (j < code.length && /[\d.xXoObBeE_a-fA-F]/.test(code[j])) j++;
      tokens.push({ cls: "py-num", text: code.substring(i, j) });
      i = j;
      continue;
    }

    // --- Identifiers / keywords ---
    if (/[a-zA-Z_]/.test(code[i])) {
      let j = i;
      while (j < code.length && /\w/.test(code[j])) j++;
      const word = code.substring(i, j);

      let k = j;
      while (k < code.length && code[k] === " ") k++;
      const isCall = code[k] === "(";

      if (word === "self") {
        tokens.push({ cls: "py-self", text: word });
      } else if (PY_KEYWORDS.has(word)) {
        tokens.push({ cls: "py-kw", text: word });
      } else if (PY_BUILTINS.has(word)) {
        tokens.push({ cls: "py-builtin", text: word });
      } else if (isCall) {
        tokens.push({ cls: "py-fn", text: word });
      } else {
        tokens.push({ cls: null, text: word });
      }
      i = j;
      continue;
    }

    // --- Decorators ---
    if (code[i] === "@" && (i === 0 || code[i - 1] === "\n" || /\s/.test(code[i - 1]))) {
      let j = i + 1;
      while (j < code.length && /[\w.]/.test(code[j])) j++;
      tokens.push({ cls: "py-builtin", text: code.substring(i, j) });
      i = j;
      continue;
    }

    // --- Everything else ---
    tokens.push({ cls: null, text: code[i] });
    i++;
  }

  let html = "";
  for (const tok of tokens) {
    const safe = esc(tok.text);
    html += tok.cls ? `<span class="${tok.cls}">${safe}</span>` : safe;
  }
  return html;
}

// ---------------------------------------------------------------------------
// HTML syntax highlighting
// ---------------------------------------------------------------------------

const HTML_VOID_TAGS = new Set([
  "area","base","br","col","embed","hr","img","input","link","meta",
  "param","source","track","wbr",
]);

export function highlightHtml(code) {
  const tokens = [];
  let i = 0;

  while (i < code.length) {
    // --- Razor / server-side blocks: @{ ... }, @model, @if, @Html.Raw(...) etc ---
    if (code[i] === "@") {
      // @{ block } — scan to matching }
      if (code[i + 1] === "{") {
        let depth = 0, j = i;
        do {
          if (code[j] === "{") depth++;
          else if (code[j] === "}") depth--;
          j++;
        } while (j < code.length && depth > 0);
        tokens.push({ cls: "hl-razor", text: code.substring(i, j) });
        i = j;
        continue;
      }
      // @(...) inline expression
      if (code[i + 1] === "(") {
        let depth = 0, j = i + 1;
        do {
          if (code[j] === "(") depth++;
          else if (code[j] === ")") depth--;
          j++;
        } while (j < code.length && depth > 0);
        tokens.push({ cls: "hl-razor", text: code.substring(i, j) });
        i = j;
        continue;
      }
      // @keyword or @Variable.Method(...)
      let j = i + 1;
      while (j < code.length && /[\w.]/.test(code[j])) j++;
      // include trailing (...) if present
      if (j < code.length && code[j] === "(") {
        let depth = 0;
        do {
          if (code[j] === "(") depth++;
          else if (code[j] === ")") depth--;
          j++;
        } while (j < code.length && depth > 0);
      }
      if (j > i + 1) {
        tokens.push({ cls: "hl-razor", text: code.substring(i, j) });
        i = j;
        continue;
      }
    }

    // --- Comment <!-- ... --> ---
    if (code.substring(i, i + 4) === "<!--") {
      const end = code.indexOf("-->", i + 4);
      const slice = end === -1 ? code.substring(i) : code.substring(i, end + 3);
      tokens.push({ cls: "hl-comment", text: slice });
      i += slice.length;
      continue;
    }

    // --- KO comment <!-- ko ... --> or <!-- /ko --> ---
    // (already handled by comment above, but we highlight it differently)

    // --- Tags < ... > ---
    if (code[i] === "<") {
      // Closing tag or opening tag
      let j = i + 1;
      const isClosing = code[j] === "/";
      if (isClosing) j++;

      // Tag name
      const nameStart = j;
      while (j < code.length && /[a-zA-Z0-9_:-]/.test(code[j])) j++;
      const tagName = code.substring(nameStart, j);

      if (tagName) {
        // Opening bracket + slash + tagName
        tokens.push({ cls: "hl-bracket", text: code.substring(i, nameStart) });
        tokens.push({ cls: "hl-tag", text: tagName });
        i = j;

        // Attributes until > or />
        while (i < code.length && code[i] !== ">") {
          // Self-closing />
          if (code[i] === "/" && code[i + 1] === ">") {
            tokens.push({ cls: "hl-bracket", text: "/>" });
            i += 2;
            break;
          }

          // Whitespace
          if (/\s/.test(code[i])) {
            tokens.push({ cls: null, text: code[i] });
            i++;
            continue;
          }

          // Razor in attribute position
          if (code[i] === "@") {
            let k = i + 1;
            if (code[k] === "(") {
              let depth = 0;
              do {
                if (code[k] === "(") depth++;
                else if (code[k] === ")") depth--;
                k++;
              } while (k < code.length && depth > 0);
            } else {
              while (k < code.length && /[\w.]/.test(code[k])) k++;
              if (k < code.length && code[k] === "(") {
                let depth = 0;
                do {
                  if (code[k] === "(") depth++;
                  else if (code[k] === ")") depth--;
                  k++;
                } while (k < code.length && depth > 0);
              }
            }
            tokens.push({ cls: "hl-razor", text: code.substring(i, k) });
            i = k;
            continue;
          }

          // Attribute name
          const attrStart = i;
          while (i < code.length && /[a-zA-Z0-9_:@-]/.test(code[i])) i++;
          if (i > attrStart) {
            const attrName = code.substring(attrStart, i);
            const cls = attrName.startsWith("data-bind") ? "hl-ko-attr" : "hl-attr";
            tokens.push({ cls, text: attrName });
          }

          // = and value
          if (code[i] === "=") {
            tokens.push({ cls: null, text: "=" });
            i++;
            // Quoted value
            if (code[i] === '"' || code[i] === "'") {
              const q = code[i];
              let k = i + 1;
              while (k < code.length && code[k] !== q) k++;
              if (k < code.length) k++;
              tokens.push({ cls: "hl-str", text: code.substring(i, k) });
              i = k;
            } else {
              // Unquoted value
              const vStart = i;
              while (i < code.length && !/[\s>]/.test(code[i])) i++;
              if (i > vStart) tokens.push({ cls: "hl-str", text: code.substring(vStart, i) });
            }
            continue;
          }

          // Safety: if nothing consumed, advance
          if (i === attrStart) {
            tokens.push({ cls: null, text: code[i] });
            i++;
          }
        }

        // Closing bracket >
        if (code[i] === ">") {
          tokens.push({ cls: "hl-bracket", text: ">" });
          i++;
        }
        continue;
      }
    }

    // --- KO virtual element bindings in text ---
    // Already caught by comment handler

    // --- Entity &...; ---
    if (code[i] === "&") {
      let j = i + 1;
      while (j < code.length && j < i + 10 && code[j] !== ";" && code[j] !== " ") j++;
      if (code[j] === ";") {
        j++;
        tokens.push({ cls: "hl-entity", text: code.substring(i, j) });
        i = j;
        continue;
      }
    }

    // --- Plain text ---
    tokens.push({ cls: null, text: code[i] });
    i++;
  }

  let html = "";
  for (const tok of tokens) {
    const safe = esc(tok.text);
    html += tok.cls ? `<span class="${tok.cls}">${safe}</span>` : safe;
  }
  return html;
}

// ---------------------------------------------------------------------------
// JSON syntax highlighting
// ---------------------------------------------------------------------------

export function highlightJson(code) {
  const tokens = [];
  let i = 0;

  while (i < code.length) {
    // --- Strings ---
    if (code[i] === '"') {
      let j = i + 1;
      while (j < code.length && code[j] !== '"') {
        if (code[j] === "\\") j++;
        j++;
      }
      if (j < code.length) j++;
      const str = code.substring(i, j);

      // Check if this is a key (followed by :)
      let k = j;
      while (k < code.length && /\s/.test(code[k])) k++;
      const isKey = code[k] === ":";

      tokens.push({ cls: isKey ? "jn-key" : "jn-str", text: str });
      i = j;
      continue;
    }

    // --- Numbers ---
    if (/[-\d]/.test(code[i]) && (i === 0 || /[[\s,:{]/.test(code[i - 1]))) {
      let j = i;
      if (code[j] === "-") j++;
      while (j < code.length && /[\d.eE+-]/.test(code[j])) j++;
      if (j > i) {
        tokens.push({ cls: "jn-num", text: code.substring(i, j) });
        i = j;
        continue;
      }
    }

    // --- Booleans / null ---
    const rest = code.substring(i, i + 5);
    if (rest.startsWith("true") && !/\w/.test(code[i + 4] || "")) {
      tokens.push({ cls: "jn-bool", text: "true" });
      i += 4;
      continue;
    }
    if (rest.startsWith("false") && !/\w/.test(code[i + 5] || "")) {
      tokens.push({ cls: "jn-bool", text: "false" });
      i += 5;
      continue;
    }
    if (rest.startsWith("null") && !/\w/.test(code[i + 4] || "")) {
      tokens.push({ cls: "jn-null", text: "null" });
      i += 4;
      continue;
    }

    // --- Structural chars ---
    if (/[{}[\]:,]/.test(code[i])) {
      tokens.push({ cls: "jn-punct", text: code[i] });
      i++;
      continue;
    }

    // --- Everything else ---
    tokens.push({ cls: null, text: code[i] });
    i++;
  }

  let html = "";
  for (const tok of tokens) {
    const safe = esc(tok.text);
    html += tok.cls ? `<span class="${tok.cls}">${safe}</span>` : safe;
  }
  return html;
}

// ---------------------------------------------------------------------------
// Language dispatcher
// ---------------------------------------------------------------------------

export function highlightCode(code, language) {
  if (language === "python") return highlightPython(code);
  if (language === "html") return highlightHtml(code);
  if (language === "json") return highlightJson(code);
  if (language === "xml") return highlightDocTags(code);
  return esc(code);
}

/** Highlight SAP CPQ document generation tags. */
function highlightDocTags(code) {
  return esc(code)
    // <<TAG_NAME(...)>> — structural tags
    .replace(/&lt;&lt;(\w+?)(\([^)]*\))?(,[^&]*)?&gt;&gt;/g, (_, name, params, mod) =>
      `<span class="dt-tag">&lt;&lt;${name}</span>` +
      (params ? `<span class="dt-param">${params}</span>` : '') +
      (mod ? `<span class="dt-mod">${mod}</span>` : '') +
      `<span class="dt-tag">&gt;&gt;</span>`)
    // <*CTX(...)*> — context tags
    .replace(/&lt;\*CTX\(([^*]*)\)\*&gt;/g,
      '<span class="dt-ctx">&lt;*CTX($1)*&gt;</span>')
    // <*Eval|Table|CO_INFO|Number(...)*> — inline functions
    .replace(/&lt;\*(Eval|Table|CO_INFO|Number)\(([^*]*)\)\*&gt;/g,
      '<span class="dt-fn">&lt;*$1($2)*&gt;</span>')
    // [IF], [EQ], [AND], [CONDITION], etc. — conditions
    .replace(/\[(IF|ENDIF|EQ|NEQ|AND|OR|NOT|IN|GEQ|LEQ|INR|BEGW|LIKE|CONDITION[^\]]*|CONDITION_END)\]/g,
      '<span class="dt-cond">[$1]</span>');
}

/**
 * Render code content into a DocumentFragment of .code-line elements.
 * Shared by file viewer tabs and search preview.
 */
export function renderCodeLines(content, language, highlightLine) {
  const lines = content.split('\n');
  const frag = document.createDocumentFragment();
  for (let i = 0; i < lines.length; i++) {
    const div = document.createElement('div');
    div.className = 'code-line' + (i + 1 === highlightLine ? ' highlight' : '');
    const highlighted = highlightCode(lines[i], language);
    div.innerHTML = `<span class="code-num">${i + 1}</span><span class="code-content">${highlighted}</span>`;
    frag.appendChild(div);
  }
  return frag;
}
